import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactFlow, { Background, Controls, useEdgesState, useNodesState, useReactFlow } from 'reactflow'
import PlusNode from './PlusNode'
import AnyAllNode from './AnyAllNode'
import ConditionNode from './ConditionNode'
import { useTheme } from '@emotion/react'
import { RuleColors, getColorFromResult } from './ruler_utils'

const RuleVisualEditor = ({ conditions, setConditions, nodesPositions, testResult, setNodesPositions, fitView, children }) => {
  const reactFlowWrapper = useRef()
  const reactFlowInstance = useReactFlow()
  const [targetNode, setTargetNode] = useState(null)
  const [dragInfo, setDragInfo] = useState(null)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges] = useEdgesState([])

  const theme = useTheme()

  const nodeTypes = useMemo(() => ({
    PlusNode,
    AnyAllNode,
    ConditionNode
  }), [])

  useEffect(() => {
    reactFlowInstance.fitView()
  }, [fitView, reactFlowInstance])

  const onNodeDelete = useCallback((nodeId) => {
    setConditions((oldCondition) => {
      const newCondition = { ...oldCondition }
      let item = newCondition
      if (!nodeId.includes('-')) {
        return null
      }
      const path = nodeId.split('-')
      const lastPath = path.pop()
      let index = null
      if (lastPath === 'condition') {
        index = path.pop()
      }
      for (let i = 0; i < path.length; i++) {
        item = item[path[i]]
      }
      if (index === null) {
        delete item[lastPath]
      } else {
        item.splice(index, 1)
      }
      return newCondition
    })
    setNodesPositions((oldPositions) => {
      const newPositions = { ...oldPositions }
      delete newPositions[nodeId]
      return oldPositions
    })
  }, [setConditions, setNodesPositions])

  const createEdge = (parentNode, childNode, dashed) => {
    const color = childNode.type !== 'PlusNode' ? parentNode.data.any ? RuleColors.any.border : RuleColors.all.border : undefined
    return {
      id: parentNode.id + '#' + childNode.id,
      source: parentNode.id,
      target: childNode.id,
      animated: false,
      sourceHandle: 'start',
      targetHandle: 'end',
      style: {
        strokeWidth: dashed ? 1.5 : 3,
        strokeDasharray: dashed ? 5 : 'initial',
        stroke: getColorFromResult(color, childNode.data.testResult)
      }
    }
  }

  const createNode = useCallback((parentNode, newNodeIdSuffix, nodeType, data = {}, index, testResult) => {
    // Find position
    const id = parentNode ? parentNode.id + '-' + (index !== undefined && index !== null ? index + '-' : '') + newNodeIdSuffix : newNodeIdSuffix
    let position = nodesPositions[id]
    if (!position) {
      const newPositions = { ...nodesPositions }
      position = { x: parentNode ? parentNode.position.x + (index === 0 ? -100 : 100) : 0, y: parentNode ? parentNode.position.y + 100 : 0 }
      newPositions[id] = position
      setNodesPositions(newPositions)
    }

    return {
      id,
      type: nodeType,
      data: { ...data, initial: !parentNode, testResult, nodeId: id, onNodeDelete },
      position
    }
  }, [nodesPositions, setNodesPositions, onNodeDelete])

  const getResult = useCallback((currentResult) => {
    if (!currentResult) {
      return null
    }
    if ('all' in currentResult) {
      for (let i = 0; i < currentResult.all.length; i++) {
        if (!getResult(currentResult.all[i])) {
          return false
        }
      }
      return true
    }
    if ('any' in currentResult) {
      for (let i = 0; i < currentResult.any.length; i++) {
        if (getResult(currentResult.any[i])) {
          return true
        }
      }
      return false
    }
    return currentResult.result
  }, [])

  const checkCondition = useCallback((condition, parentNode, nodes, edges, index, testResult) => {
    if (!condition) {
      // Allow another plus
      const newNode = createNode(parentNode, 'plus', 'PlusNode', {}, index)
      nodes.push(newNode)
      if (parentNode) {
        edges.push(createEdge(parentNode, newNode, true))
      }
      return
    }

    if ('any' in condition || 'all' in condition) {
      const array = 'any' in condition ? condition.any : condition.all
      const testArray = testResult ? 'any' in condition ? testResult.any : testResult.all : null

      const newNode = createNode(parentNode, 'any' in condition ? 'any' : 'all', 'AnyAllNode', { any: 'any' in condition }, index, getResult(testResult))
      nodes.push(newNode)
      if (parentNode) {
        edges.push(createEdge(parentNode, newNode, false))
      }

      array.forEach((item, childIndex) => {
        checkCondition(item, newNode, nodes, edges, childIndex, testArray ? testArray[childIndex] : null)
      })

      // Add plus if less than two items
      if (array.length < 2) {
        const index = array.length
        const plusNode = createNode(newNode, 'plus', 'PlusNode', {}, index)
        nodes.push(plusNode)
        edges.push(createEdge(newNode, plusNode, true))
      }
      return
    }

    if ('fact' in condition) {
      const newNode = createNode(parentNode, 'condition', 'ConditionNode', condition, index, testResult?.result)
      nodes.push(newNode)
      edges.push(createEdge(parentNode, newNode, false))
    }
  }, [createNode, getResult])

  useEffect(() => {
    const nodes = []
    const edges = []
    checkCondition(conditions, null, nodes, edges, null, testResult?.conditions)
    setNodes(nodes)
    setEdges(edges)
  }, [conditions, setNodes, checkCondition, setEdges, testResult])

  const updatePositionsOnNodeReplace = (replaceNode, newNodeID) => {
    // Update positions
    const newPositions = { ...nodesPositions }
    newPositions[newNodeID] = newPositions[replaceNode.id]
    delete newPositions[replaceNode.id]
    setNodesPositions(newPositions)
  }

  const addNode = (nodeType, nodeId, nodeCode, nodeName, replaceNode, nodePositionPercentage) => {
    if (replaceNode.id === 'plus') {
      // First node can only be any or all
      if (nodeType !== 'node' || (nodeCode !== 'any' && nodeCode !== 'all')) {
        return
      }
      conditions = {}
      conditions[nodeCode] = []
      updatePositionsOnNodeReplace(replaceNode, nodeCode)
      setConditions(conditions)
      return
    }

    // Find condition to replace
    const path = replaceNode.id.split('-')
    const originalPath = [...path]
    const lastPath = path.pop()
    const lastIndex = path.pop()
    let item = conditions
    path.forEach((pathItem) => { item = item[pathItem] })

    if (lastPath === 'all' || lastPath === 'any') {
      // Replacing an all or any node is not allowed
      return
    }

    if (lastPath === 'condition') {
      if (nodeType !== 'condition' && nodeType !== 'formula') {
        // If condition node type we can only replace with a condition or formula
        return
      }
      const condition = item[lastIndex]
      if (nodeType === 'formula') {
        if (nodePositionPercentage.left < 0.5) {
          condition.fact = { type: nodeType, id: nodeId, name: nodeName }
        } else {
          condition.value = { type: nodeType, id: nodeId, name: nodeName }
        }
      } else if (nodeType === 'condition') {
        condition.operator = nodeCode
      }
      setConditions({ ...conditions })
      return
    }

    // We are changing a plus for what we dropped
    let newNodeIdSufix = ''

    let newCondition = {}
    if (nodeType === 'node') {
      newCondition[nodeCode] = []
      newNodeIdSufix = nodeCode
    } else if (nodeType === 'condition') {
      newNodeIdSufix = 'condition'
      if ('fact' in item) {
        newCondition = { ...item }
      } else {
        newCondition = {
          fact: null,
          operator: null,
          value: null
        }
      }
      newCondition.operator = nodeCode
    } else if (nodeType === 'rule' || nodeType === 'formula') {
      newNodeIdSufix = 'condition'
      newCondition = {
        fact: { type: nodeType, id: nodeId, name: nodeName },
        operator: nodeType === 'rule' ? 'equal' : null,
        value: nodeType === 'rule' ? true : null
      }
    }
    originalPath.pop()

    updatePositionsOnNodeReplace(replaceNode, originalPath.join('-') + '-' + newNodeIdSufix)

    item.push(newCondition)
    setConditions({ ...conditions })
  }

  const getNodeFromPosition = useCallback((reactFlowPosition) => {
    const overlapingNode = nodes.map((node) => reactFlowInstance.getNode(node.id)).find((node) => {
      if (node.position.x > reactFlowPosition.x) {
        return false
      }
      if (node.position.x + node.width < reactFlowPosition.x) {
        return false
      }
      if (node.position.y > reactFlowPosition.y) {
        return false
      }
      if (node.position.y + node.height < reactFlowPosition.y) {
        return false
      }
      return true
    }
    )

    return overlapingNode
  }, [nodes, reactFlowInstance])

  useEffect(() => {
    // whenever the target changes, we swap the colors
    setNodes((nodes) =>
      nodes.map((node) => {
        return {
          ...node,
          data: { ...node.data, hover: (targetNode && node.id === targetNode.id), dragInfo }
        }
      })
    )
  }, [targetNode, setNodes, dragInfo])

  const getNodePositionPercentage = (node, position) => {
    const leftPercentage = (position.x - node.position.x) / node.width
    return { left: leftPercentage }
  }

  const onDragOver = useCallback((event) => {
    const position = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY })
    const overlapingNode = getNodeFromPosition(position)
    if (overlapingNode) {
      if (!targetNode || (targetNode.id !== overlapingNode.id)) {
        setTargetNode(overlapingNode)
      }
      if (overlapingNode.type === 'ConditionNode' && (!overlapingNode.data || !overlapingNode.data.fact || !overlapingNode.data.fact.type || overlapingNode.data.fact.type !== 'rule')) {
        const type = event.dataTransfer.types.find((type) => type.startsWith('smartpm/rule/section/name/'))
        setDragInfo({ type: type.substring('smartpm/rule/section/name/'.length), position: getNodePositionPercentage(overlapingNode, position) })
      }
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    } else if (targetNode) {
      setTargetNode(null)
    }
  }, [targetNode, getNodeFromPosition, reactFlowInstance])

  return (

    <div ref={reactFlowWrapper} style={{ height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
        onDragOver={onDragOver}
        onNodesChange={(events) => {
          events = events.filter((event) => {
            if (event.type === 'remove') {
              return false
            }
            return true
          })
          events.forEach((event) => {
            if (event.dragging) {
              nodesPositions[event.id] = event.position
            }
          })
          onNodesChange(events)
        }}
        fitView
        onDrop={(event) => {
          const position = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY })
          const overlapingNode = getNodeFromPosition(position)
          if (!overlapingNode) {
            return
          }
          event.preventDefault()
          addNode(
            event.dataTransfer.getData('smartpm/rule/section'),
            event.dataTransfer.getData('smartpm/rule/id'),
            event.dataTransfer.getData('smartpm/rule/code'),
            event.dataTransfer.getData('smartpm/rule/name'),
            overlapingNode,
            getNodePositionPercentage(overlapingNode, position)
          )
          setTargetNode(null)
        }}
        style={{ backgroundColor: theme.palette.background.row2 }}
      ><Controls />
        <Background />
        {children}
      </ReactFlow>
    </div>
  )
}

export default RuleVisualEditor
